import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, Home, RefreshCw, Heart } from 'lucide-react';
import { Header } from '../components/Header';
import { GlassCard } from '../components/ui/GlassCard';
import { DonateModal } from '../components/DonateModal';
export function DonationErrorPage() {
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  return <div className="min-h-screen bg-black text-white flex flex-col" data-id="element-983">
      {/* Background */}
      <div className="fixed inset-0 z-0 pointer-events-none" data-id="element-984">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-full blur-[120px]" data-id="element-985" />
        <div className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-gradient-to-tl from-purple-500/10 to-pink-500/10 rounded-full blur-[120px]" data-id="element-986" />
      </div>

      <Header data-id="element-987" />

      <div className="relative z-10 flex-1 flex items-center justify-center px-4 py-12" data-id="element-988">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="w-full max-w-2xl" data-id="element-989">
          <GlassCard className="p-12 text-center" data-id="element-990">
            {/* Error Icon */}
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.2,
            type: 'spring',
            stiffness: 200
          }} className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/30 mb-6" data-id="element-991">
              <AlertCircle size={48} className="text-red-400" data-id="element-992" />
            </motion.div>

            {/* Heading */}
            <motion.h1 initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="text-4xl md:text-5xl font-bold text-white mb-4 font-special" data-id="element-993">
              Payment Issue
            </motion.h1>

            {/* Message */}
            <motion.p initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="text-gray-400 text-lg mb-8 leading-relaxed" data-id="element-994">
              We encountered an issue processing your donation. Don't worry, no
              charges were made to your account.
            </motion.p>

            {/* Common Issues */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="text-left p-6 rounded-lg bg-white/5 border border-white/10 mb-8" data-id="element-995">
              <h3 className="text-sm font-bold text-white mb-3" data-id="element-996">
                Common issues:
              </h3>
              <ul className="text-sm text-gray-400 space-y-2" data-id="element-997">
                <li className="flex items-start gap-2" data-id="element-998">
                  <span className="text-red-400 mt-0.5" data-id="element-999">•</span>
                  <span data-id="element-1000">Payment was cancelled or timed out</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-1001">
                  <span className="text-red-400 mt-0.5" data-id="element-1002">•</span>
                  <span data-id="element-1003">Insufficient funds or card declined</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-1004">
                  <span className="text-red-400 mt-0.5" data-id="element-1005">•</span>
                  <span data-id="element-1006">Network connection interrupted</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-1007">
                  <span className="text-red-400 mt-0.5" data-id="element-1008">•</span>
                  <span data-id="element-1009">PayPal account verification required</span>
                </li>
              </ul>
            </motion.div>

            {/* Action Buttons */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-1010">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-cyan-400 to-purple-400 hover:from-cyan-500 hover:to-purple-500 text-white font-bold transition-all" style={{
              fontFamily: 'Space Grotesk, sans-serif'
            }} data-id="element-1011">
                <Home size={20} data-id="element-1012" />
                <span data-id="element-1013">Back to Home</span>
              </Link>
            </motion.div>

            {/* Help Note */}
            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.7
          }} className="text-sm text-gray-500 mt-8" data-id="element-1014">
              Need help? Contact us at{' '}
              <a href="mailto:support@faind.com" className="text-cyan-400 hover:text-cyan-300 transition-colors" data-id="element-1015">
                support@faind.com
              </a>
            </motion.p>
          </GlassCard>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-6 bg-black/80 backdrop-blur-lg" data-id="element-1016">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-1017">
          <div className="flex items-center gap-2" data-id="element-1018">
            <span style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontWeight: 300,
            fontSize: '1.125rem',
            lineHeight: '1.75rem',
            letterSpacing: '.15em'
          }} className="text-white" data-id="element-1019">
              FAIND
            </span>
          </div>
          <div className="text-gray-500 text-sm" data-id="element-1020">
            © {currentYear} Faind. All rights reserved.
          </div>
          <div className="flex items-center gap-6" data-id="element-1021">
            <a href="https://x.com/faindyourapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Follow us on X" data-id="element-1022">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" data-id="element-1023">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" data-id="element-1024" />
              </svg>
            </a>
            <button onClick={() => setIsDonateModalOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-pink-500/30 text-gray-400 hover:text-pink-400 transition-all text-sm font-medium" data-id="element-1025">
              <Heart size={14} data-id="element-1026" />
              Donate
            </button>
          </div>
        </div>
      </footer>

      <DonateModal isOpen={isDonateModalOpen} onClose={() => setIsDonateModalOpen(false)} data-id="element-1027" />
    </div>;
}